import type { AnalyticsEventName, AnalyticsProps, AuthMethod } from './events';

export type { AnalyticsEventName, AnalyticsProps, AuthMethod };

export interface AnalyticsIdentifyTraits {
  userId?: number | null;
  userGuid?: string | null;
  platform?: string;
  /** Given / first name for person display in PostHog. */
  firstName?: string | null;
  /** Handle / username. */
  userName?: string | null;
  /** Account email when known. */
  email?: string | null;
  /**
   * App environment. Prefer omitting at call sites — the analytics client
   * attaches the value from `init({ appEnv })` automatically.
   */
  appEnv?: string | null;
}

/** Map APP_ENV → short PostHog label (dev / stag / prod). */
export const toAnalyticsAppEnv = (appEnv: string | null | undefined): string => {
  const normalized = (appEnv ?? '').trim().toLowerCase();
  if (normalized === 'development' || normalized === 'dev') {
    return 'dev';
  }
  if (normalized === 'staging' || normalized === 'stag') {
    return 'stag';
  }
  if (normalized === 'production' || normalized === 'prod') {
    return 'prod';
  }
  return normalized || 'unknown';
};

export interface AnalyticsAttributionProperties {
  ad_source?: string;
  media_source?: string;
  campaign?: string;
  campaign_id?: string;
  adset?: string;
  ad?: string;
  af_channel?: string;
  af_status?: string;
}

/**
 * Branch attribution person-properties for PostHog.
 *
 * Deliberately namespaced with a `branch_` prefix so Branch NEVER overwrites the
 * AppsFlyer-sourced attribution properties (`campaign`, `media_source`, …) that
 * `setAttribution` / `onInstallConversionData` already own. AppsFlyer remains the
 * MMP; Branch is an additional, independent attribution layer.
 */
export interface AnalyticsBranchAttributionProperties {
  /** Branch campaign (`~campaign`). */
  branch_campaign?: string;
  /** Branch channel (`~channel`), e.g. facebook, tiktok, google. */
  branch_channel?: string;
  /** Branch feature (`~feature`). */
  branch_feature?: string;
  /** Branch funnel stage (`~stage`). */
  branch_stage?: string;
  /** Branch link tags (`~tags`) joined with commas. */
  branch_tags?: string;
  /** Campaign id when Branch resolves paid-ad attribution (`~campaign_id`). */
  branch_campaign_id?: string;
  /** Ad partner / advertising partner name when present. */
  branch_ad_partner?: string;
  /** Resolved Branch link URL (`~referring_link` / `+url`). */
  branch_referring_link?: string;
  /** True when the session was opened from a real Branch link. */
  branch_clicked_link?: boolean;
  /** True on the very first session (install) for this device. */
  branch_first_session?: boolean;
}

export interface AnalyticsInitConfig {
  posthogApiKey: string;
  posthogHost: string;
  appsFlyerDevKey: string;
  /** iOS Apple App ID (numeric). Ignored on Android. */
  appsFlyerAppId: string;
  /** TikTok Events Manager App ID. Empty skips TikTok. */
  tiktokAppId?: string;
  /** TikTok Events Manager access token. Empty skips TikTok. */
  tiktokAppSecret?: string;
  /** GA4 web Measurement ID. Empty or invalid values disable web analytics. */
  ga4MeasurementId?: string;
  /** Identifier-free initial route used for GA4's default URL context. */
  initialPagePath?: string;
  /** Identifier-free referrer URL, or an external origin without path/query. */
  initialPageReferrer?: string;
  appEnv: string;
  isDebug?: boolean;
}

export type WebAnalyticsEventName =
  | 'post_created'
  | 'comment_created'
  | 'post_liked'
  | 'buddy_added'
  | 'search';

export interface AnalyticsClient {
  init: (config: AnalyticsInitConfig) => Promise<void>;
  identify: (
    distinctId: string,
    traits?: AnalyticsIdentifyTraits,
  ) => void;
  reset: () => void;
  track: (event: AnalyticsEventName, properties?: AnalyticsProps) => void;
  /** Web GA4 custom event. No-op on native platforms. */
  trackEvent: (
    event: WebAnalyticsEventName,
    properties?: AnalyticsProps,
  ) => void;
  /** Web GA4 page view. No-op on native platforms. */
  trackPageView: (
    pagePath: string,
    properties?: AnalyticsProps,
  ) => void;
  /** Fire once per install/user (guarded by storage key). */
  trackOnce: (
    storageKey: string,
    event: AnalyticsEventName,
    properties?: AnalyticsProps,
  ) => Promise<void>;
  screen: (screenName: string, properties?: AnalyticsProps) => void;
  setAttribution: (properties: AnalyticsAttributionProperties) => void;
  /**
   * Sets Branch attribution person-properties. Kept separate from
   * `setAttribution` so Branch and AppsFlyer attribution never clobber each
   * other. No-op on web.
   */
  setBranchAttribution: (
    properties: AnalyticsBranchAttributionProperties,
  ) => void;
}

export const resolveDistinctId = (
  userGuid: string | null | undefined,
  userId: number | null | undefined,
): string | null => {
  if (userGuid && userGuid.trim()) {
    return userGuid.trim();
  }
  if (userId != null && Number.isFinite(userId)) {
    return String(userId);
  }
  return null;
};

export const toAuthMethod = (
  provider: 'Google' | 'Apple' | 'Facebook' | 'email' | string,
): AuthMethod => {
  const normalized = provider.trim().toLowerCase();
  if (
    normalized === 'email' ||
    normalized === 'google' ||
    normalized === 'apple' ||
    normalized === 'facebook'
  ) {
    return normalized;
  }
  return 'email';
};
